import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogPosts } from '../data/blogData'; // Add this import

// This should match the data from Blog.jsx - ideally move to a shared data file

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    // Find the current post
    const currentPost = blogPosts.find(p => p.id === parseInt(id));
    
    if (currentPost) {
      setPost(currentPost);
      
      // Find related posts (same category, excluding current post)
      const related = blogPosts
        .filter(p => p.id !== currentPost.id && p.category === currentPost.category)
        .slice(0, 3);
      
      setRelatedPosts(related);
    }

    // Scroll to top when component mounts or ID changes
    window.scrollTo(0, 0);
  }, [id]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatContent = (content) => {
    // Convert markdown-style formatting to HTML
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
      .replace(/\n\n/g, '</p><p>') // Paragraphs
      .replace(/\n- /g, '<li>') // List items
      .replace(/(<li>.*?)(?=<li>|<\/p>|$)/g, '<ul>$1</ul>') // Wrap list items in ul
      .replace(/<\/ul><ul>/g, '') // Clean up adjacent ul tags
      .replace(/(<li>.*?)<\/p>/g, '$1</li></ul><p>') // Fix list ending
      .replace(/^/, '<p>') // Add opening p tag
      .replace(/$/, '</p>'); // Add closing p tag
  };

  if (!post) {
    return (
      <div className="blog-post-error">
        <div className="container">
          <h1>Post Not Found</h1>
          <p>Sorry, the blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="btn btn-primary">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <article className="blog-post">
      <div className="blog-post-header">
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/blog">Blog</Link>
            <span>›</span>
            <span>{post.title}</span>
          </nav>
          
          <div className="post-meta">
            <span className="category">{post.category}</span>
            <span className="date">{formatDate(post.date)}</span>
            <span className="read-time">{post.readTime}</span>
          </div>
          
          <h1>{post.title}</h1>
          <p className="post-excerpt">{post.excerpt}</p>
          
          <div className="author-info">
            <span>By {post.author}</span>
          </div>
        </div>
      </div>

      <div className="blog-post-image">
        <img src={post.image} alt={post.title} />
      </div>

      <div className="blog-post-content">
        <div className="container">
          <div 
            className="content"
            dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
          />
          
          <div className="post-footer">
            <div className="share-buttons">
              <span>Share this article:</span>
              <button 
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: post.title,
                      text: post.excerpt,
                      url: window.location.href,
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="share-btn"
              >
                Share
              </button>
            </div>
            
            <div className="navigation-buttons">
              <Link to="/blog" className="btn btn-outline">
                ← Back to Blog
              </Link>
              <Link to="/#contact" className="btn btn-primary">
                Get Quote
              </Link>
            </div>
          </div>
        </div>
      </div>

      {relatedPosts.length > 0 && (
        <div className="related-posts">
          <div className="container">
            <h2>Related Articles</h2>
            <div className="related-grid">
              {relatedPosts.map(relatedPost => (
                <article key={relatedPost.id} className="related-card">
                  <Link to={`/blog/${relatedPost.id}`}>
                    <img src={relatedPost.image} alt={relatedPost.title} />
                    <div className="related-content">
                      <h3>{relatedPost.title}</h3>
                      <p>{relatedPost.excerpt}</p>
                      <span className="read-more">Read More →</span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default BlogPost;